import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/auth";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // Correct the import
import MenuIcon from "@mui/icons-material/Menu"; // Menu icon for toggling sidebar on mobile
import LogoutIcon from "@mui/icons-material/Logout"; // Logout icon for the sidebar
import logo from "../assets/Ralph_logo.png";
import Sidebar from "./Sidebar";

const Topbar = ({ isOperator: isOperatorProp }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if the screen is mobile
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [username, setUsername] = useState("");
  const [entity, setEntity] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Check if user is an Operator directly from localStorage
  const [isOperator, setIsOperator] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem('role');
    console.log('Topbar - Current role:', role);
    setIsOperator(role === 'Operator');
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decodedToken = jwtDecode(token);
          setUsername(decodedToken.username);

          const response = await axios.get(
            `${baseUrl}/auth/user-info`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          setEntity(response.data.entity);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, [isAuthenticated]);

  const handleLogout = () => {
    // Clear all user data from localStorage
    localStorage.removeItem("bookmarkId");
    localStorage.removeItem("permissions");
    localStorage.removeItem("role");
    logout();
    navigate("/");
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  // Drawer content for mobile
  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleSidebar}
      onKeyDown={toggleSidebar}
    >
      <List>
        {/* Username */}
        <ListItem>
          <ListItemText primary="Username" secondary={username} />
        </ListItem>

        {/* Logout Option */}
        <ListItem button onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2 }} />
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {/* Show the MenuIcon only on mobile and NOT for Operators */}
          {isMobile && !isOperator && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleSidebar}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo Box */}
          <Box sx={{ mr: 2, pt: 1, pb: 1 }}>
            <Avatar
              variant="rounded"
              src={logo}
              alt="Logo"
              sx={{ width: 50, height: 50 }}
            />
          </Box>

          {/* App Title */}
          <Box
            sx={{
              flexGrow: 1,
              color: "#000000",
              fontWeight: "bold",
              padding: "10px",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: {
                  xs: "0.8rem", // Extra small devices (phones)
                  sm: "1.1rem", // Small devices (tablets)
                  md: "1.5rem", // Medium devices (laptops)
                  lg: "2.0rem", // Large devices (desktops)
                },
              }}
            >
              Liberia Motorcycle & Tricycle Security Database
            </Typography>
          </Box>

          {/* User Info and Avatar - Show logout button on mobile, full menu on desktop */}
          {isMobile ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handleLogout}
                sx={{ color: 'inherit' }}
                title="Logout"
              >
                <LogoutIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 1 }}>
                {username}
              </Typography>
              <IconButton onClick={handleMenuOpen}>
                <Avatar alt={username} src="/path/to/avatar.jpg" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar >

      {/* Mobile Sidebar Drawer - Hide for Operators */}
      {!isOperator && (
        < Drawer anchor="left" open={showSidebar} onClose={toggleSidebar} >
          {drawerContent}
        </Drawer >
      )}

      {/* Desktop Sidebar - Hide for Operators */}
      {!isOperator && (
        < Sidebar
          showSidebar={showSidebar}
          toggleSidebar={toggleSidebar}
        />
      )}
    </>
  );
};

export default Topbar;
